import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { softClose, saveSoftClose, errText } from "@/lib/bidCenterApi";

const rulesText = (s) => `Soft close: if a valid bid is placed in the last ${s.window_min} minutes before the auction closes, bidding is extended so the close is at least ${s.ext_min} minutes after that bid. This repeats until ${s.ext_min} minutes pass with no new valid bid. Bid times are taken from Facebook's comment time.`;

const Num = ({ id, v, on }) => <Input type="number" min={1} max={60} data-testid={id} value={v} onChange={(e) => on(Number(e.target.value) || 1)} className="w-20 h-9" />;

function Override({ hid, o, name, on, onRemove }) {
  return (
    <div data-testid={`soft-override-${hid}`} className="flex flex-wrap items-center gap-2 text-sm rounded-lg border p-2">
      <b className="min-w-[8rem]">{name || hid}</b>
      <Switch data-testid={`soft-override-enabled-${hid}`} checked={o.enabled} onCheckedChange={(v) => on({ ...o, enabled: v })} />
      window <Num id={`soft-override-window-${hid}`} v={o.window_min} on={(v) => on({ ...o, window_min: v })} /> min · extend <Num id={`soft-override-ext-${hid}`} v={o.ext_min} on={(v) => on({ ...o, ext_min: v })} /> min
      <Button size="sm" variant="ghost" data-testid={`soft-override-remove-${hid}`} onClick={onRemove}><Trash2 className="h-4 w-4" /></Button>
    </div>
  );
}

export const SoftCloseSettings = ({ auctions, onSaved }) => {
  const [s, setS] = useState(null);
  const [pick, setPick] = useState("");
  useEffect(() => { softClose().then(setS).catch((e) => toast.error(errText(e))); }, []);
  if (!s) return null;
  const names = Object.fromEntries(auctions.map((a) => [a.horse_id, a.horse_name]));
  const setOv = (hid, o) => setS({ ...s, overrides: { ...s.overrides, [hid]: o } });
  const save = async () => {
    try { setS(await saveSoftClose(s)); toast.success("Soft-close settings saved."); onSaved(); } catch (e) { toast.error(errText(e)); }
  };
  const copy = () => navigator.clipboard?.writeText(rulesText(s)).then(() => toast.success("Buyer rules copied"));
  return (
    <details data-testid="soft-close-settings" className="rounded-xl border bg-white p-3">
      <summary className="cursor-pointer font-semibold text-sm">Soft-close rules · {s.enabled ? `on (${s.window_min} min window, +${s.ext_min} min)` : "off"}</summary>
      <div className="space-y-3 pt-3">
        <label className="flex items-center justify-between text-sm">Soft close enabled<Switch data-testid="soft-enabled" checked={s.enabled} onCheckedChange={(v) => setS({ ...s, enabled: v })} /></label>
        <label className="flex items-center gap-2 text-sm">Soft-close window <Num id="soft-window" v={s.window_min} on={(v) => setS({ ...s, window_min: v })} /> minutes before close</label>
        <label className="flex items-center gap-2 text-sm">Extend to <Num id="soft-ext" v={s.ext_min} on={(v) => setS({ ...s, ext_min: v })} /> minutes after the bid</label>
        <div className="space-y-2">
          <p className="text-xs text-stone-500">Per-auction overrides</p>
          {Object.entries(s.overrides).map(([hid, o]) => <Override key={hid} hid={hid} o={o} name={names[hid]} on={(v) => setOv(hid, v)} onRemove={() => { const { [hid]: _, ...rest } = s.overrides; setS({ ...s, overrides: rest }); }} />)}
          <div className="flex gap-2">
            <select data-testid="soft-override-pick" value={pick} onChange={(e) => setPick(e.target.value)} className="h-9 rounded-md border px-2 text-sm flex-1 min-w-0">
              <option value="">Choose an auction…</option>
              {auctions.filter((a) => !s.overrides[a.horse_id]).map((a) => <option key={a.horse_id} value={a.horse_id}>{a.horse_name}</option>)}
            </select>
            <Button variant="outline" data-testid="soft-override-add" disabled={!pick} onClick={() => { setOv(pick, { enabled: s.enabled, window_min: s.window_min, ext_min: s.ext_min }); setPick(""); }} className="h-9">Add override</Button>
          </div>
        </div>
        <div className="rounded-lg bg-stone-50 p-2 text-xs space-y-1">
          <p className="font-semibold">Buyer explanation (for ads and the group)</p>
          <p data-testid="soft-rules-text">{rulesText(s)}</p>
          <Button size="sm" variant="outline" data-testid="soft-rules-copy" onClick={copy} className="h-8"><Copy className="h-3 w-3 mr-1" />Copy</Button>
        </div>
        <p className="text-[11px] text-stone-500">Changes recalculate every auction's close from its bids. Existing Facebook posts are never edited automatically.</p>
        <Button data-testid="soft-save" onClick={save} className="h-11 sm:h-9">Save soft-close settings</Button>
      </div>
    </details>
  );
};
